/* ========== EXTENDED CHART PARSER ==========
 * Parses the modern chartEx XML format used by Treemap, Sunburst,
 * Funnel, Waterfall, Box & Whisker, Histogram, Pareto, Filled Map,
 * and Clustered Column (when authored via the modern API).
 *
 * Unlike the standard <c:chart> format, chartEx stores data references
 * separately under <cx:chartData><cx:data><cx:strDim>/<cx:numDim>, and
 * we have to read the actual cell values from the workbook to populate
 * the series.
 * ============================================ */

import { qLocal, qLocalAll, qDirectChild } from "../xmlUtils";
import { CHART_EX_LAYOUT_MAP } from "./constants";
import { readRangeValues, readRangeAsGrid } from "./workbookContext";
import { resolveSpPrColor } from "./colorResolver";

/** Pull the chart title from <cx:title>. Same fallbacks as standardChart. */
function readExtendedTitle(chartDoc) {
  const titleNode = qLocal(chartDoc, "title");
  if (!titleNode) return "";
  const vNode = qLocal(titleNode, "v");
  if (vNode && vNode.textContent.trim()) return vNode.textContent.trim();
  const tNodes = qLocalAll(titleNode, "t");
  if (tNodes.length > 0) {
    return tNodes.map(t => t.textContent || "").join("").trim();
  }
  return "";
}

/**
 * Read the series name. chartEx stores it under <cx:tx><cx:v> as a literal,
 * or under <cx:tx><cx:f> as a cell-reference formula we have to dereference.
 */
async function readExtendedSeriesName(series, zip, ctx) {
  const txNode = qLocal(series, "tx");
  if (!txNode) return "Series 1";

  const vNode = qLocal(txNode, "v");
  if (vNode && vNode.textContent.trim()) return vNode.textContent.trim();

  const fNode = qLocal(txNode, "f");
  if (fNode) {
    const fText = fNode.textContent.trim();
    const formula = ctx.definedNames[fText] || fText;
    const vals = await readRangeValues(zip, ctx, formula);
    if (vals.length > 0) return vals[0];
  }
  return "Series 1";
}

/**
 * Read the cached <cx:lvl><cx:pt idx="N">value</cx:pt> points of a dim.
 * Returns an array of levels, each level being an idx-dense string array.
 * chartEx always caches the data inline, so this works even when the
 * cx:f range reference cannot be resolved against the workbook.
 */
function readCachedLevels(dim) {
  const lvlNodes = qLocalAll(dim, "lvl");
  return lvlNodes.map(lvl => {
    const expected = parseInt(lvl.getAttribute("ptCount") || "0", 10);
    const indexed = [];
    let maxIdx = -1;
    qLocalAll(lvl, "pt").forEach(pt => {
      const idx = parseInt(pt.getAttribute("idx") || "0", 10);
      indexed[idx] = (pt.textContent || "").trim();
      if (idx > maxIdx) maxIdx = idx;
    });
    const total = Math.max(expected, maxIdx + 1);
    const out = [];
    for (let i = 0; i < total; i++) out.push(indexed[i] != null ? indexed[i] : "");
    return out;
  });
}

/**
 * Walk chartData blocks and resolve every strDim (categories) and numDim
 * (values) reference into actual cell values.
 *
 * Priority per dim:
 *   1. Live cell range read via cx:f (keeps multi-column hierarchy
 *      orientation exactly as authored on the sheet).
 *   2. Cached <cx:lvl> points embedded in the chart XML — always present,
 *      used when the range read fails (renamed sheet, defined name, …).
 *
 * For multi-column / multi-level categories (Treemap/Sunburst hierarchy)
 * we expose `hierarchy`: an array of rows where row[col] is the label at
 * that level (row[0] = top-most parent, last = leaf).
 */
async function readChartData(chartDoc, zip, ctx) {
  const out = { categories: [], values: [], hierarchy: null };
  const chartData = qLocal(chartDoc, "chartData");
  if (!chartData) return out;

  const dataBlocks = qLocalAll(chartData, "data");
  for (const dataBlock of dataBlocks) {
    const dims = qLocalAll(dataBlock, "strDim").concat(qLocalAll(dataBlock, "numDim"));
    for (const dim of dims) {
      const type = (dim.getAttribute("type") || "").toLowerCase();
      const fNode = qLocal(dim, "f");
      const ref = fNode ? (fNode.textContent || "").trim() : "";

      if (type === "cat") {
        // 1. Live range read — multi-column ranges are the hierarchy.
        let gotLive = false;
        if (ref) {
          const grid = await readRangeAsGrid(zip, ctx, ref);
          if (grid.rows.length > 0 && grid.rows.some(row => row.some(c => c !== ""))) {
            gotLive = true;
            if (grid.colCount > 1) {
              out.hierarchy = grid.rows;
              out.categories = grid.rows.map(row => row[row.length - 1]);
            } else {
              out.categories = grid.rows.map(row => row[0] || "");
            }
          }
        }
        // 2. Cached levels fallback. Levels are ordered leaf-first (same
        //    convention as c:multiLvlStrCache), so reverse for hierarchy
        //    rows [parent, ..., leaf].
        if (!gotLive) {
          const lvls = readCachedLevels(dim);
          if (lvls.length === 1) {
            out.categories = lvls[0];
          } else if (lvls.length > 1) {
            const count = Math.max.apply(null, lvls.map(l => l.length).concat([0]));
            const rows = [];
            for (let i = 0; i < count; i++) {
              const row = [];
              for (let l = lvls.length - 1; l >= 0; l--) row.push(lvls[l][i] || "");
              rows.push(row);
            }
            out.hierarchy = rows;
            out.categories = rows.map(row => row[row.length - 1]);
          }
        }
      } else if (type === "val" || type === "size" || type === "colorval" || type === "y") {
        const toNum = (v) => {
          const n = parseFloat(v);
          return isNaN(n) ? 0 : n;
        };
        let cells = ref ? await readRangeValues(zip, ctx, ref) : [];
        if (!cells.length || cells.every(v => v === "")) {
          const lvls = readCachedLevels(dim);
          cells = lvls.length > 0 ? lvls[0] : [];
        }
        out.values = cells.map(toNum);
        // Keep the source range so the viewer can re-read LIVE values after
        // a reader edits the sheet (see utils/chartLiveData.js).
        if (ref && type === "val") out.valuesRef = ref;
      }
    }
  }
  return out;
}

/**
 * Waterfall subtotal indices: <cx:series><cx:layoutPr><cx:subtotals>
 * <cx:idx val="N"/>…  Points at those indices are totals — they restart
 * from zero instead of stacking on the running sum.
 */
function readSubtotals(seriesNode) {
  const layoutPr = qLocal(seriesNode, "layoutPr");
  if (!layoutPr) return [];
  const subtotalsNode = qLocal(layoutPr, "subtotals");
  if (!subtotalsNode) return [];
  return qLocalAll(subtotalsNode, "idx")
    .map(n => parseInt(n.getAttribute("val") || "-1", 10))
    .filter(v => v >= 0);
}

/**
 * Histogram binning rules: <cx:layoutPr><cx:binning binSize="…"> or
 * <cx:binCount val="…"/>. Returns { binSize?, binCount?, intervalClosed? }
 * or null when no binning element is authored.
 */
function readBinning(seriesNode) {
  const layoutPr = qLocal(seriesNode, "layoutPr");
  if (!layoutPr) return null;
  const binningNode = qLocal(layoutPr, "binning");
  if (!binningNode) return null;
  const out = { intervalClosed: binningNode.getAttribute("intervalClosed") || "r" };
  const binSizeNode = qLocal(binningNode, "binSize");
  const binCountNode = qLocal(binningNode, "binCount");
  if (binSizeNode) {
    const v = parseFloat(binSizeNode.getAttribute("val") || binSizeNode.textContent || "");
    if (!isNaN(v) && v > 0) out.binSize = v;
  }
  if (binCountNode) {
    const v = parseInt(binCountNode.getAttribute("val") || binCountNode.textContent || "", 10);
    if (!isNaN(v) && v > 0) out.binCount = v;
  }
  return out;
}

export async function parseExtendedChart(chartDoc, zip, ctx) {
  const result = {
    title:      readExtendedTitle(chartDoc),
    chartType:  "bar",
    isCombo:    false,
    series:     [],
    categories: [],
  };

  // chartEx charts can have multiple <cx:series> nodes — Pareto in
  // particular has TWO: a "clusteredColumn" data series plus a "paretoLine"
  // overlay. We need to look at all of them to decide the renderer.
  const seriesNodes = qLocalAll(chartDoc, "series");
  if (seriesNodes.length === 0) {
    if (!result.title) result.title = "Chart";
    return result;
  }

  const layouts = seriesNodes.map(s => s.getAttribute("layoutId") || "");

  // Pareto = clusteredColumn + paretoLine combo. Route to the histogram
  // renderer which already knows how to overlay the cumulative-% line.
  if (layouts.indexOf("paretoLine") !== -1) {
    result.chartType = "pareto";
  } else {
    result.chartType = CHART_EX_LAYOUT_MAP[layouts[0]] || "bar";
  }
  result.layoutId = layouts[0];

  // Read data from the primary (non-overlay) series — paretoLine series
  // re-uses the primary's data via ownerIdx, so we only need to read once.
  const primarySer = seriesNodes.find(s => s.getAttribute("layoutId") !== "paretoLine") || seriesNodes[0];
  const seriesName = await readExtendedSeriesName(primarySer, zip, ctx);
  const { categories, values, hierarchy, valuesRef } = await readChartData(chartDoc, zip, ctx);

  // For filled maps, capture the cx:geography attributes so the renderer
  // can pick a country-appropriate outline (e.g. India for cultureRegion="IN").
  if (result.chartType === "filledMap") {
    const geoNode = qLocal(primarySer, "geography") || qLocal(chartDoc, "geography");
    if (geoNode) {
      result.geography = {
        cultureRegion: geoNode.getAttribute("cultureRegion") || "",
        cultureLanguage: geoNode.getAttribute("cultureLanguage") || "",
        attribution: geoNode.getAttribute("attribution") || "",
      };
    }
  }

  // Waterfall subtotal indices + histogram binning rules
  const subtotals = readSubtotals(primarySer);
  if (subtotals.length > 0) result.subtotals = subtotals;
  const binning = readBinning(primarySer);
  if (binning) result.binning = binning;

  // Authored series color (cx:series > cx:spPr > a:solidFill)
  const seriesColor = resolveSpPrColor(qDirectChild(primarySer, "spPr") || qLocal(primarySer, "spPr"));

  if (values.length > 0) {
    const ser = { name: seriesName, data: values, renderType: result.chartType };
    if (hierarchy) ser.hierarchy = hierarchy;
    if (seriesColor) ser.color = seriesColor;
    if (valuesRef) ser.valuesRef = valuesRef;
    result.series.push(ser);
  }
  result.categories = categories;
  if (hierarchy) result.hierarchy = hierarchy;
  if (!result.title) result.title = seriesName || "Chart";

  return result;
}
